/**
 * SectionCard - The shared card chrome used by every form "section" molecule
 * (Billing, Settings, Assignment, Customer Contact, Customer/Location, Project
 * Information, Inspection Notes, …).
 *
 * Until this existed, each of those molecules re-declared the exact same
 * wrapper — `bg-white shadow rounded-lg border border-brand-border p-24px`
 * with an identical `text-18px font-heading font-semibold text-brand-heading
 * mb-16px` heading. Centralizing it here means card padding, border, and
 * heading treatment retune in one place and every section stays aligned.
 *
 * Consumers compose this as their root and pass their own BEM block class via
 * `className` plus their molecule-level marker via `dataRef`, so each section
 * keeps its structural identity while sharing the chrome.
 *
 * @example
 * <SectionCard title="Billing" className="billing-section" dataRef="molecule-billing-section">
 *   {fields}
 * </SectionCard>
 */
import React from 'react';

export interface SectionCardProps {
	/** Heading shown at the top of the card. Omit for a heading-less card. */
	title?: React.ReactNode;
	/** Card body content. */
	children: React.ReactNode;
	/** Extra classes merged onto the root <section> (consumer block + any extras). */
	className?: string;
	/** Extra classes for the heading, when a consumer needs to tweak it. */
	headingClassName?: string;
	/**
	 * Overrides the data-ref marker so a consuming section keeps its own
	 * identity (e.g. "molecule-billing-section"). Defaults to the SectionCard
	 * marker for direct use.
	 */
	dataRef?: string;
}

export const SectionCard: React.FC<SectionCardProps> = ({ title, children, className = '', headingClassName = '', dataRef = 'molecule-section-card' }) => {
	return (
		<section className={`section-card bg-white shadow rounded-lg border border-brand-border p-24px ${className}`} data-ref={dataRef}>
			{title && (
				<h2 className={`section-card__heading text-18px font-heading font-semibold text-brand-heading mb-16px ${headingClassName}`}>{title}</h2>
			)}
			{children}
		</section>
	);
};

export default SectionCard;
